import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';

import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { circlePolygon, destinationPoint, haversineDistanceMeters } from '@/utils/geodesy';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

export const RADIAL_SOURCE_ID = 'orbit-radial-source';

// Matches the DMA/surveyed-area outline convention (colour, width, dash) so the tool's
// geometry reads as part of the same visual family. Map layer paints cannot reference
// CSS variables, so the Origin Blue token is hardcoded here like every other layer.
const ORIGIN_BLUE = '#007CFF';

export type RadialPhase = 'awaiting-centre' | 'measuring' | 'set';

export interface RadialMeasurement {
    phase: RadialPhase;
    /** [longitude, latitude] of the measurement centre; null until placed. */
    centre: [number, number] | null;
    /** [longitude, latitude] of the radius edge (the drag handle); null until placed. */
    handle: [number, number] | null;
    radiusMeters: number;
    isDraggingHandle: boolean;
}

const INITIAL_RADIAL_MEASUREMENT: RadialMeasurement = {
    phase: 'awaiting-centre',
    centre: null,
    handle: null,
    radiusMeters: 0,
    isDraggingHandle: false,
};

/** The one shared source: circle polygon + radial line + centre/handle points, tagged by `part`. */
function buildRadialFeatureCollection(measurement: RadialMeasurement): FeatureCollection {
    const { centre, handle, radiusMeters } = measurement;
    if (measurement.phase === 'awaiting-centre' || !centre || !handle) return EMPTY_FEATURE_COLLECTION;

    const features: Feature[] = [
        { type: 'Feature', properties: { part: 'circle' }, geometry: circlePolygon(centre, radiusMeters) },
        {
            type: 'Feature',
            properties: { part: 'radial-line' },
            geometry: { type: 'LineString', coordinates: [centre, handle] },
        },
        { type: 'Feature', properties: { part: 'centre' }, geometry: { type: 'Point', coordinates: centre } },
        { type: 'Feature', properties: { part: 'handle' }, geometry: { type: 'Point', coordinates: handle } },
    ];
    return { type: 'FeatureCollection', features };
}

function registerRadialSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection): void {
    try {
        if (mlMap.getSource(RADIAL_SOURCE_ID)) {
            (mlMap.getSource(RADIAL_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(RADIAL_SOURCE_ID, { type: 'geojson', data });
        }
    } catch {
        return;
    }

    const layers: maplibregl.LayerSpecification[] = [
        {
            id: ORBIT_LAYER_IDS.radialFill,
            type: 'fill',
            source: RADIAL_SOURCE_ID,
            filter: ['==', ['get', 'part'], 'circle'],
            paint: { 'fill-color': ORIGIN_BLUE, 'fill-opacity': 0.08 },
        },
        {
            id: ORBIT_LAYER_IDS.radialOutline,
            type: 'line',
            source: RADIAL_SOURCE_ID,
            filter: ['==', ['get', 'part'], 'circle'],
            paint: { 'line-color': ORIGIN_BLUE, 'line-width': 1.5 },
        },
        {
            id: ORBIT_LAYER_IDS.radialLine,
            type: 'line',
            source: RADIAL_SOURCE_ID,
            filter: ['==', ['get', 'part'], 'radial-line'],
            paint: { 'line-color': ORIGIN_BLUE, 'line-width': 1.5, 'line-dasharray': [3, 3] },
        },
        {
            id: ORBIT_LAYER_IDS.radialCentre,
            type: 'circle',
            source: RADIAL_SOURCE_ID,
            filter: ['==', ['get', 'part'], 'centre'],
            paint: {
                'circle-radius': 4,
                'circle-color': ORIGIN_BLUE,
                'circle-stroke-color': '#FFFFFF',
                'circle-stroke-width': 1.5,
            },
        },
        {
            // Invisible, generously sized drag target under the visible handle: circle layers
            // hit-test on their full circle-radius regardless of opacity, so this is what makes
            // the handle easy to grab without bloating the visible dot.
            id: ORBIT_LAYER_IDS.radialHandleHit,
            type: 'circle',
            source: RADIAL_SOURCE_ID,
            filter: ['==', ['get', 'part'], 'handle'],
            paint: { 'circle-radius': 16, 'circle-opacity': 0 },
        },
        {
            // Styled identically to the centre dot (8px Origin Blue with a white ring) per
            // the design — the oversized invisible hit layer above keeps it easy to grab.
            id: ORBIT_LAYER_IDS.radialHandle,
            type: 'circle',
            source: RADIAL_SOURCE_ID,
            filter: ['==', ['get', 'part'], 'handle'],
            paint: {
                'circle-radius': 4,
                'circle-color': ORIGIN_BLUE,
                'circle-stroke-color': '#FFFFFF',
                'circle-stroke-width': 1.5,
            },
        },
    ];

    for (const layer of layers) {
        if (!mlMap.getLayer(layer.id)) {
            try {
                addOrbitLayer(mlMap, layer, 'interaction');
            } catch {
                // Progressive enhancement — layer failures must not break the map.
            }
        }
    }
}

/**
 * A touch tap cannot rubber-band a radius (there is no hover), so it creates the circle
 * at a starter radius the user then adjusts by dragging the handle: a quarter of the
 * viewport's shorter span, rounded to two significant figures so the label starts clean.
 */
function defaultTouchRadiusMeters(mlMap: maplibregl.Map, centre: [number, number]): number {
    const bounds = mlMap.getBounds();
    const widthMeters = haversineDistanceMeters([bounds.getWest(), centre[1]], [bounds.getEast(), centre[1]]);
    const heightMeters = haversineDistanceMeters([centre[0], bounds.getSouth()], [centre[0], bounds.getNorth()]);
    const quarter = Math.min(widthMeters, heightMeters) / 4;
    if (!Number.isFinite(quarter) || quarter <= 0) return 100;
    const magnitude = 10 ** Math.floor(Math.log10(quarter));
    return Math.round(quarter / (magnitude / 10)) * (magnitude / 10);
}

/**
 * The radial measuring tool (PBI 250/354). While `active`, this hook owns every map
 * click — MapView suppresses feature interaction and click dismissal for the duration:
 *
 *   awaiting-centre — crosshair cursor; a click places the centre and starts measuring.
 *   measuring       — the radius rubber-bands after the pointer (rAF-throttled); a second
 *                     click freezes it. A touch tap skips this phase entirely and lands on
 *                     `set` at a starter radius (no hover to rubber-band with).
 *
 * The cursor tells the grab story: crosshair only while placing the centre; the closed
 * hand (`grabbing`) whenever the radius edge is following the pointer — rubber-band
 * measuring included, where the first click leaves the edge held without the button
 * down; the open hand (`grab`) only when hovering the settled handle, offering the
 * next grab.
 *   set             — geometry persists and the map pans/zooms normally (the circle is
 *                     geographic, so it re-projects for free). Dragging the handle resizes
 *                     the radius live (map panning disabled only mid-drag); clicking
 *                     elsewhere restarts the measurement at the new point.
 *
 * Escape clears the current measurement back to awaiting-centre, or — when there is
 * nothing to clear — deactivates the tool via `onDeactivate`. Deactivation (button
 * toggle, a Locate command, client switch, unmount) empties the source, detaches every
 * listener and restores the cursor; layers stay registered (empty and invisible), per
 * the register-once convention.
 *
 * Geometry updates go through `setData` on the one shared source — sources and layers
 * are never removed/re-added during interaction — and survive basemap switches by
 * re-registering from the current measurement on `style.load`.
 */
export function useRadialMeasureTool(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    active: boolean,
    onDeactivate: () => void,
): RadialMeasurement {
    const [measurement, setMeasurement] = useState<RadialMeasurement>(INITIAL_RADIAL_MEASUREMENT);
    const measurementRef = useRef(measurement);

    const onDeactivateRef = useRef(onDeactivate);
    useEffect(() => {
        onDeactivateRef.current = onDeactivate;
    });

    useEffect(() => {
        if (!active || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerRadialSourceAndLayers(mlMap, buildRadialFeatureCollection(measurementRef.current));
        };
        register();
        mlMap.on('style.load', register);

        const setCursor = (cursor: string) => {
            mlMap.getCanvas().style.cursor = cursor;
        };
        setCursor('crosshair');

        // Single mutation path: ref (for event handlers), React state (for the label) and
        // the GeoJSON source (for the map) always change together and can never disagree.
        const apply = (next: RadialMeasurement) => {
            measurementRef.current = next;
            setMeasurement(next);
            setGeoJsonSourceData(mlMap, RADIAL_SOURCE_ID, buildRadialFeatureCollection(next));
        };

        // Pointer moves are rAF-throttled: the latest lngLat wins and everything (distance,
        // circle, line, label) updates at most once per frame, keeping rapid drags cheap.
        let rafId: number | null = null;
        let pendingLngLat: [number, number] | null = null;
        const schedulePointerUpdate = (lngLat: [number, number]) => {
            pendingLngLat = lngLat;
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const target = pendingLngLat;
                pendingLngLat = null;
                const current = measurementRef.current;
                if (!target || !current.centre) return;
                apply({ ...current, handle: target, radiusMeters: haversineDistanceMeters(current.centre, target) });
            });
        };

        // A handle drag ends on mouseup, whose trailing click must not be read as
        // "restart the measurement here".
        let suppressNextClick = false;

        // Tracked so a drag that ends with the pointer still on the handle settles back
        // to the open hand — mouseenter will not re-fire without leaving first.
        let isPointerOverHandle = false;

        const handleMapClick = (e: maplibregl.MapMouseEvent) => {
            if (suppressNextClick) {
                suppressNextClick = false;
                return;
            }
            const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
            const current = measurementRef.current;

            if (current.phase === 'measuring' && current.centre) {
                apply({
                    ...current,
                    phase: 'set',
                    handle: lngLat,
                    radiusMeters: haversineDistanceMeters(current.centre, lngLat),
                });
                setCursor('');
                return;
            }

            // awaiting-centre, or a click away from the handle in `set`: (re)place the centre.
            // Touch taps go straight to `set` at a starter radius (handle due east, the
            // EditableCircle convention); anything without a pointerType is treated as mouse.
            const pointerType = (e.originalEvent as Partial<PointerEvent> | undefined)?.pointerType;
            if (pointerType === 'touch') {
                const radius = defaultTouchRadiusMeters(mlMap, lngLat);
                apply({
                    phase: 'set',
                    centre: lngLat,
                    handle: destinationPoint(lngLat, radius, 90),
                    radiusMeters: radius,
                    isDraggingHandle: false,
                });
                setCursor('');
            } else {
                apply({ phase: 'measuring', centre: lngLat, handle: lngLat, radiusMeters: 0, isDraggingHandle: false });
                // The centre is down and the pointer is now carrying the radius edge —
                // held, even though the button is up, so the hand is closed.
                setCursor('grabbing');
            }
        };

        const handlePointerMove = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
            const current = measurementRef.current;
            if (current.phase !== 'measuring' && !current.isDraggingHandle) return;
            schedulePointerUpdate([e.lngLat.lng, e.lngLat.lat]);
        };

        const startHandleDrag = () => {
            const current = measurementRef.current;
            if (current.phase !== 'set' || current.isDraggingHandle) return;
            // MapLibre would otherwise pan the map along with the handle.
            mlMap.dragPan.disable();
            apply({ ...current, isDraggingHandle: true });
            setCursor('grabbing');
        };

        const endHandleDrag = () => {
            const current = measurementRef.current;
            if (!current.isDraggingHandle) return;
            mlMap.dragPan.enable();
            suppressNextClick = true;
            apply({ ...current, isDraggingHandle: false });
            setCursor(isPointerOverHandle ? 'grab' : '');
        };

        const handleHandleEnter = () => {
            isPointerOverHandle = true;
            const current = measurementRef.current;
            if (current.phase === 'set' && !current.isDraggingHandle) setCursor('grab');
        };
        const handleHandleLeave = () => {
            isPointerOverHandle = false;
            const current = measurementRef.current;
            if (current.phase === 'set' && !current.isDraggingHandle) setCursor('');
        };

        // Staged Escape (the legacy ruler's semantics): clear the measurement first;
        // with nothing left to clear, a further Escape deactivates the tool.
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            const current = measurementRef.current;
            if (current.phase === 'awaiting-centre') {
                onDeactivateRef.current();
                return;
            }
            if (current.isDraggingHandle) {
                mlMap.dragPan.enable();
                // The release of the abandoned drag still produces a click, which must
                // not immediately place a new centre.
                suppressNextClick = true;
            }
            apply(INITIAL_RADIAL_MEASUREMENT);
            setCursor('crosshair');
        };

        mlMap.on('click', handleMapClick);
        mlMap.on('mousemove', handlePointerMove);
        mlMap.on('touchmove', handlePointerMove);
        mlMap.on('mouseup', endHandleDrag);
        mlMap.on('touchend', endHandleDrag);
        mlMap.on('mousedown', ORBIT_LAYER_IDS.radialHandleHit, startHandleDrag);
        mlMap.on('touchstart', ORBIT_LAYER_IDS.radialHandleHit, startHandleDrag);
        mlMap.on('mouseenter', ORBIT_LAYER_IDS.radialHandleHit, handleHandleEnter);
        mlMap.on('mouseleave', ORBIT_LAYER_IDS.radialHandleHit, handleHandleLeave);
        window.addEventListener('keydown', handleKeyDown);
        // A drag released outside the canvas never fires the map's mouseup/touchend.
        window.addEventListener('mouseup', endHandleDrag);
        window.addEventListener('touchend', endHandleDrag);

        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
            if (measurementRef.current.isDraggingHandle) mlMap.dragPan.enable();
            mlMap.off('style.load', register);
            mlMap.off('click', handleMapClick);
            mlMap.off('mousemove', handlePointerMove);
            mlMap.off('touchmove', handlePointerMove);
            mlMap.off('mouseup', endHandleDrag);
            mlMap.off('touchend', endHandleDrag);
            mlMap.off('mousedown', ORBIT_LAYER_IDS.radialHandleHit, startHandleDrag);
            mlMap.off('touchstart', ORBIT_LAYER_IDS.radialHandleHit, startHandleDrag);
            mlMap.off('mouseenter', ORBIT_LAYER_IDS.radialHandleHit, handleHandleEnter);
            mlMap.off('mouseleave', ORBIT_LAYER_IDS.radialHandleHit, handleHandleLeave);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('mouseup', endHandleDrag);
            window.removeEventListener('touchend', endHandleDrag);
            setCursor('');
            // Reset for the next activation: empty the source (layers stay registered,
            // invisible with nothing to draw) and drop the measurement.
            measurementRef.current = INITIAL_RADIAL_MEASUREMENT;
            setMeasurement(INITIAL_RADIAL_MEASUREMENT);
            setGeoJsonSourceData(mlMap, RADIAL_SOURCE_ID, EMPTY_FEATURE_COLLECTION);
        };
    }, [active, isMapLoaded, mapRef]);

    return measurement;
}
